import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { types as sdkTypes } from '../util/sdkLoader';
import { denormalisedResponseEntities } from '../util/data';

const { UUID } = sdkTypes;

// How far ahead to look for the next available slot.
const LOOKAHEAD_DAYS = 30;
// Cache TTL — re-using a fetched slot for 5 minutes is a sensible compromise between
// freshness on a search results page and avoiding hammering /timeslots on every render.
const CACHE_TTL_MS = 5 * 60 * 1000;
const SLOT_PAGE_SIZE = 25;

const startOfDayInTz = (date, tz) => {
  // We just need a stable lookahead; sub-second precision is irrelevant. Using the
  // raw Date works for any tz and avoids pulling in moment-timezone for one helper.
  const d = new Date(date);
  d.setUTCHours(0, 0, 0, 0);
  return d;
};

/**
 * Fetch the next bookable timeslot for a listing.
 *
 * - Looks up to 30 days ahead.
 * - Returns the timeslot's `start` Date (or null when nothing is available).
 * - Cached per-listing so multiple cards rendered for the same listing share one fetch.
 *
 * NOTE: this is intentionally separate from ListingPage's monthly time slots fetcher,
 * which paginates a full booking calendar. We only need the *next* slot for the card.
 */
export const fetchListingNextSlot = createAsyncThunk(
  'listingNextSlot/fetch',
  async ({ listingId, force = false }, { getState, rejectWithValue, extra: sdk }) => {
    if (!listingId || !sdk) {
      return rejectWithValue('missing-listing-id');
    }
    const id = typeof listingId === 'string' ? listingId : listingId.uuid;
    const state = getState().listingNextSlot?.byId?.[id];
    const now = Date.now();
    if (!force && state?.fetchedAt && now - state.fetchedAt < CACHE_TTL_MS) {
      return { id, nextSlot: state.nextSlot };
    }

    const start = new Date(now);
    const end = new Date(now + LOOKAHEAD_DAYS * 24 * 60 * 60 * 1000);

    try {
      const response = await sdk.timeslots.query({
        listingId: new UUID(id),
        start,
        end,
        perPage: SLOT_PAGE_SIZE,
        page: 1,
      });
      const slots = denormalisedResponseEntities(response) || [];
      const earliest = slots
        .map(s => s?.attributes?.start)
        .filter(d => d instanceof Date && !Number.isNaN(d.getTime()) && d.getTime() > now)
        .sort((a, b) => a.getTime() - b.getTime())[0];

      return { id, nextSlot: earliest ? earliest.toISOString() : null };
    } catch (e) {
      return rejectWithValue(e?.message || 'next-slot-fetch-failed');
    }
  },
  {
    // Avoid duplicate concurrent dispatches for the same listing.
    condition: ({ listingId, force = false }, { getState }) => {
      if (force) return true;
      const id = typeof listingId === 'string' ? listingId : listingId?.uuid;
      if (!id) return false;
      const entry = getState().listingNextSlot?.byId?.[id];
      const now = Date.now();
      if (entry?.inFlight) return false;
      if (entry?.fetchedAt && now - entry.fetchedAt < CACHE_TTL_MS) return false;
      return true;
    },
  }
);

const slice = createSlice({
  name: 'listingNextSlot',
  initialState: {
    byId: {},
  },
  extraReducers: builder => {
    builder
      .addCase(fetchListingNextSlot.pending, (state, action) => {
        const id =
          typeof action.meta.arg.listingId === 'string'
            ? action.meta.arg.listingId
            : action.meta.arg.listingId?.uuid;
        if (!id) return;
        state.byId[id] = { ...(state.byId[id] || {}), inFlight: true, error: null };
      })
      .addCase(fetchListingNextSlot.fulfilled, (state, action) => {
        const { id, nextSlot } = action.payload || {};
        if (!id) return;
        state.byId[id] = {
          nextSlot: nextSlot || null,
          fetchedAt: Date.now(),
          inFlight: false,
          error: null,
        };
      })
      .addCase(fetchListingNextSlot.rejected, (state, action) => {
        const id =
          typeof action.meta.arg.listingId === 'string'
            ? action.meta.arg.listingId
            : action.meta.arg.listingId?.uuid;
        if (!id) return;
        state.byId[id] = {
          ...(state.byId[id] || {}),
          inFlight: false,
          error: action.payload || 'unknown',
        };
      });
  },
});

export const selectListingNextSlot = (state, listingId) => {
  const id = typeof listingId === 'string' ? listingId : listingId?.uuid;
  if (!id) return null;
  return state.listingNextSlot?.byId?.[id] || null;
};

export default slice.reducer;
